import { useState, type KeyboardEvent } from 'react';
import { Building, Check, Loader2 } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { Button } from '@/components/ui/button';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { displayOrganizationNumber } from '@financialharmony/tax-se';
import { filterCompaniesByName } from '@/lib/filterCompaniesByName';
import type { CompanyOverview } from '@/types/company';

interface CompanyPickerProps {
  companies: CompanyOverview[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  currentCompanyId: string | undefined;
  /** Id of the company being switched to, if a switch is in flight. */
  pendingCompanyId: string | null;
  /** True while any switch (including to the platform admin view) is in flight. */
  switching: boolean;
  onSelect: (company: CompanyOverview) => void;
}

// The picker lives inside a Radix menu, which would otherwise treat typed characters as
// menu typeahead and arrow keys as menu navigation. Escape and Tab are left alone so the
// menu can still close and move focus.
const keepKeyInPicker = (event: KeyboardEvent) => {
  if (event.key !== 'Escape' && event.key !== 'Tab') event.stopPropagation();
};

export const CompanyPicker = ({
  companies,
  isLoading,
  isError,
  onRetry,
  currentCompanyId,
  pendingCompanyId,
  switching,
  onSelect,
}: CompanyPickerProps) => {
  const t = useTranslations('consultantShell.companyPicker');
  const tCompany = useTranslations('consultantShell.changeCompany');
  const [query, setQuery] = useState('');

  if (isLoading) {
    return (
      <div role="status" className="flex w-72 items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('loading')}
      </div>
    );
  }

  if (isError || !companies) {
    return (
      <div role="alert" className="flex w-72 flex-col items-center gap-3 px-4 py-6 text-center">
        <p className="text-sm text-muted-foreground">{t('loadFailed')}</p>
        <Button size="sm" variant="outline" onClick={onRetry}>
          {t('retry')}
        </Button>
      </div>
    );
  }

  const visible = filterCompaniesByName(companies, query);

  return (
    <Command shouldFilter={false} label={t('searchLabel')} className="w-72" onKeyDown={keepKeyInPicker}>
      {companies.length > 1 && (
        <CommandInput
          autoFocus
          value={query}
          onValueChange={setQuery}
          placeholder={t('searchPlaceholder')}
        />
      )}
      <CommandList label={t('searchLabel')} className="max-h-64">
        {visible.length === 0 ? (
          <div role="status" className="px-4 py-6 text-center text-sm text-muted-foreground">
            {companies.length === 0 ? t('none') : t('noMatch', { query: query.trim() })}
          </div>
        ) : (
          <CommandGroup>
            {visible.map((company) => {
              const isCurrent = company.id === currentCompanyId;
              const isPending = company.id === pendingCompanyId;
              return (
                <CommandItem
                  key={company.id}
                  value={company.id}
                  disabled={isCurrent || switching}
                  onSelect={() => onSelect(company)}
                  className="gap-3"
                >
                  <Building size={16} className="shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1" data-ph-mask>
                    <p className="truncate text-sm font-medium">{company.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{tCompany('organization', { number: displayOrganizationNumber(company.organizationNumber) })}</p>
                  </div>
                  {isPending && <Loader2 role="status" aria-label={t('switching')} className="h-4 w-4 shrink-0 animate-spin" />}
                  {isCurrent && <Check className="h-4 w-4 shrink-0 text-primary" aria-label={t('currentCompany')} />}
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
};
